import { CompetitionTag } from '@/components/Competition'
import { ExpandableBlock, LoadingView } from '@/components/FormElement'
import { ImgFallback } from '@/components/Image'
import { useLanguage } from '@/hooks/Contexts'
import { useTick } from '@/hooks/Countdown'
import { API_URL } from '@/utils/Consts'
import { svgs } from '@/utils/Images'
import { appUtils } from 'lib/AppUtils'
import Link from 'next/link'
import { FC, Fragment, useEffect, useMemo, useState } from 'react'

export const UserCompetitions: FC = () => {
  const {
    competitions: { active_comp, no_active_comp, go_to_shop },
  } = useLanguage()

  const [loading, setLoading] = useState(true)
  const [list, setList] = useState([])

  useEffect(() => {
    appUtils
      .fetcher(API_URL.allUserTix, false)
      .then(res => {
        const arr = []
        if (!(Array.isArray(res) && res.length == 0)) {
          for (const key in res) {
            if (arr.findIndex(x => x.id == key) == -1) {
              arr.push({ ...res[key], id: key })
            }
          }
        }
        setList(arr)
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [])

  return (
    <ExpandableBlock title={active_comp} titleTxtCls='text-cyan-v2' openDefault={true} innerCls='relative !items-center !justify-center flex-col text-center'>
      <LoadingView show={loading} extraCls='rounded' />
      {list?.length > 0 ? (
        list.map(item => (
          <div key={item.id} className='mt-4 flex flex-col border-t border-cyan-v2 pt-2 first:mt-0'>
            <Item data={item} />
          </div>
        ))
      ) : (
        <>
          <div className={loading ? 'opacity-0' : ''}>{no_active_comp}</div>
          <Link href={'/competitions'}>
            <button type='button' className={`btn-rd-outline btn-hover-amber btn-width mt-2 h-10${loading ? ' opacity-0' : ''}`}>
              {go_to_shop}
            </button>
          </Link>
        </>
      )}
    </ExpandableBlock>
  )
}

const Item: FC<{ data: Record<string, any> }> = ({ data }) => {
  const {
    competitions: { ends_in },
  } = useLanguage()

  const { countDown } = useTick(['00', '00', '00', '00'], ['days', 'hours', 'minutes', 'seconds'], data.end_time)

  const winner_number = useMemo(() => data.winner_number?.split?.(',') || [], [data.winner_number])

  const tickets = useMemo(() => {
    const tmp = []
    data.tickets?.map?.(ticket => {
      const wonInstant = !!data.instant_win_numbers?.includes?.(ticket)
      if (winner_number.length == 0 || !winner_number.includes(ticket) || wonInstant) {
        tmp.push({ number: ticket, wonInstant })
      }
    })
    return tmp
  }, [data.tickets, winner_number, data.instant_win_numbers])

  return (
    <>
      <div className='flex items-center'>
        <span className='text-left text-sm font-semibold'>{data?.title}</span>
        {winner_number?.length > 0 ? (
          <CompetitionTag
            color='#ECAA00'
            append={
              <svg viewBox='0 0 17 16' fill='none' xmlns='http://www.w3.org/2000/svg' className='h-4 w-4'>
                {svgs.trophy_solid}
              </svg>
            }
            tag={winner_number.join(', ') + ' WON'}
            className='ml-1 !inline-flex h-fit w-fit !py-0.5'
          />
        ) : null}
        {data.url ? <BuyMore url={data.url} cls='ml-auto hidden md:block' /> : null}
      </div>
      <div className='mt-2 flex'>
        <ImgFallback
          src={data?.image}
          alt={data?.title || data?.image}
          className='mr-2 max-h-24 w-24 self-baseline rounded object-contain md:mr-4 md:block md:max-h-36 md:w-48'
        />
        <div className='flex flex-1 flex-col items-start text-xs md:text-sm'>
          {/* Countdown */}
          <div className='flex items-center font-semibold'>
            <span>{ends_in}&nbsp;</span>
            <span className={countDown[0] == '00' && countDown[1] == '00' && (countDown[2] != '00' || countDown[3] != '00') ? 'text-red' : ``}>
              {countDown.map((cd, i) => (
                <Fragment key={'cd-' + i}>
                  <span>{cd}</span>
                  <span>{i > 2 ? '' : i > 0 ? ':' : ' days '}</span>
                </Fragment>
              ))}
            </span>
          </div>

          {/* Tickets */}
          <div className='mt-2 whitespace-pre-wrap text-left'>
            {tickets?.length > 0
              ? tickets.map((ticket, index) => (
                  <Fragment key={'tix-' + index}>
                    <span className='ml-0.5'>{index > 0 ? ', ' : ''}</span>
                    <span className={ticket.wonInstant ? 'font-semibold text-success' : ''}>{ticket.number}</span>
                  </Fragment>
                ))
              : null}
          </div>
          {data.url ? <BuyMore url={data.url} cls='mt-2 md:hidden' /> : null}
        </div>
      </div>
    </>
  )
}

const BuyMore: FC<{ url: string; cls: string }> = ({ url, cls }) => {
  const {
    competitions: { buy_more },
  } = useLanguage()
  return (
    <Link href={url}>
      <button className={'clickable-text text-xs font-semibold md:text-sm ' + cls} type='button'>
        {buy_more + ' >'}
      </button>
    </Link>
  )
}

export default UserCompetitions
